/**
 * Expanded Chart Modal Component
 * 
 * Professional-grade signal detail view with:
 * - Full-width chart at top with extended time ranges (30D, 90D, 1Y, 5Y, MAX)
 * - Dynamic stats that update based on selected range
 * - Data availability messaging
 * - Tabbed intelligence sections below
 */

import { el } from '../../shared/components/simpleRender.js';
import { createExpandedChart } from './SparklineChart.js';
import { fetchSignalHistory } from '../../api/capitulators.js';
import { getSignalIntelligence } from '../data/signalIntelligence.js';
import { generateNarrative } from '../data/narrativeGenerator.js';
import { createExportDropdown, exportPNG, exportCSV, exportJSON, exportPDF } from '../data/exportKit.js';
import { navigateToChartMode } from '../../pages/charts.js';

let activeModal = null;
let activeChart = null;
let currentTab = 'overview';
let currentRangeId = '90d';
let currentChartData = [];  // Store chart data for exports
let currentNarrative = null;  // Store narrative for PDF export
let currentStats = null;  // Store stats for exports
let showEventsEnabled = true;  // Track events toggle state

// Time range configurations
// Note: MAX uses 3650 days (~10 years) as the API has a limit
const TIME_RANGES = [
  { id: '30d', label: '30D', days: 30 },
  { id: '90d', label: '90D', days: 90 },
  { id: '1y', label: '1Y', days: 365 },
  { id: '5y', label: '5Y', days: 1825 },
  { id: 'max', label: 'MAX', days: 3650 },
];

const STATUS_CONFIG = {
  low: { label: 'LOW', color: 'var(--tg-fog-green)', bg: 'rgba(151, 184, 160, 0.15)', icon: '🟢' },
  normal: { label: 'NORMAL', color: 'var(--tg-fog-green)', bg: 'rgba(151, 184, 160, 0.15)', icon: '🟢' },
  neutral: { label: 'NEUTRAL', color: 'var(--tg-fog-green)', bg: 'rgba(151, 184, 160, 0.15)', icon: '🟢' },
  elevated: { label: 'ELEVATED', color: '#f97316', bg: 'rgba(249, 115, 22, 0.15)', icon: '🟡' },
  high: { label: 'HIGH', color: 'var(--tg-rust-red)', bg: 'rgba(178, 51, 42, 0.15)', icon: '🟠' },
  extreme: { label: 'EXTREME', color: 'var(--tg-flash-crash)', bg: 'rgba(255, 0, 51, 0.15)', icon: '🔴' },
  tight: { label: 'TIGHT', color: 'var(--tg-rust-red)', bg: 'rgba(178, 51, 42, 0.15)', icon: '🔴' },
  loose: { label: 'LOOSE', color: 'var(--tg-fog-green)', bg: 'rgba(151, 184, 160, 0.15)', icon: '🟢' },
};

const REGIME_SIGNALS = ['LIQUIDITY_REGIME'];

const TABS = [
  { id: 'overview', label: 'Overview', icon: '💡' },
  { id: 'levels', label: 'Levels', icon: '📊' },
  { id: 'playbook', label: 'Playbook', icon: '📋' },
  { id: 'history', label: 'History', icon: '📅' },
  { id: 'correlations', label: 'Correlations', icon: '🔗' },
];

function formatValue(value, signalId) {
  if (value == null) return '—';
  if (signalId === 'NET_LIQUIDITY') return `$${(value / 1e6).toFixed(2)}T`;
  if (signalId === 'LIQUIDITY_REGIME') return String(value).toUpperCase();
  if (Math.abs(value) >= 1000) return value.toLocaleString('en-US', { maximumFractionDigits: 2 });
  if (Math.abs(value) < 0.01) return value.toFixed(4);
  return value.toFixed(2);
}

function toChartData(historyData) {
  if (!historyData || !Array.isArray(historyData)) return [];
  return historyData
    .filter(d => d.value != null)
    .map(d => {
      let timestamp;
      if (typeof d.time === 'number') timestamp = d.time;
      else if (typeof d.date === 'number') timestamp = d.date;
      else if (typeof d.date === 'string') timestamp = Math.floor(new Date(d.date).getTime() / 1000);
      else if (typeof d.time === 'string') timestamp = Math.floor(new Date(d.time).getTime() / 1000);
      else return null;
      return { time: timestamp, value: d.value };
    })
    .filter(Boolean)
    .sort((a, b) => a.time - b.time);
}

/**
 * Get the range label for stats display
 */
function getRangeLabel(rangeId) {
  const range = TIME_RANGES.find(r => r.id === rangeId);
  return range ? range.label : '';
}

/**
 * Calculate data availability info
 */
function getDataAvailability(chartData, requestedDays) {
  if (!chartData || chartData.length < 2) {
    return { hasFullRange: false, message: null };
  }
  
  const firstDate = new Date(chartData[0].time * 1000);
  const lastDate = new Date(chartData[chartData.length - 1].time * 1000);
  const actualDays = Math.round((lastDate - firstDate) / (1000 * 60 * 60 * 24));
  
  // Check if we got significantly less data than requested
  // For MAX (3650 days), always show availability since we likely won't have 10 years
  const isMaxRange = requestedDays >= 3650;
  const threshold = isMaxRange ? 0 : requestedDays * 0.8;
  const hasFullRange = actualDays >= threshold;
  
  let message = null;
  if (!hasFullRange || isMaxRange) {
    const startStr = firstDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    message = `Data starts: ${startStr}`;
  }
  
  return { hasFullRange, message, actualDays, startDate: firstDate };
}

// ===========================================================================
// Tab Content Builders
// ===========================================================================

function buildOverviewTab(intel, signal, status) {
  const statusCfg = STATUS_CONFIG[status] || STATUS_CONFIG.normal;
  
  const currentInterp = intel?.interpretation?.find(i => i.level === status);
  let currentPlaybook = intel?.playbook?.find(p => p.level === status);
  if (!currentPlaybook && intel?.playbook?.length) currentPlaybook = intel.playbook[0];
  
  return el('div', { className: 'tab-content tab-content--overview' }, [
    intel?.whyItMatters ? el('div', { className: 'intel-card' }, [
      el('h4', { className: 'intel-card__title' }, ['💡 Why This Matters']),
      el('p', { className: 'intel-card__text' }, [intel.whyItMatters]),
    ]) : null,
    
    currentInterp ? el('div', { className: 'intel-card intel-card--highlight', style: `border-color: ${statusCfg.color}` }, [
      el('div', { className: 'current-status-header' }, [
        el('span', { className: 'current-status-icon' }, [statusCfg.icon]),
        el('span', { className: 'current-status-level', style: `color: ${statusCfg.color}` }, [`Currently: ${status.toUpperCase()}`]),
        el('span', { className: 'current-status-range' }, [currentInterp.range]),
      ]),
      el('div', { className: 'current-status-meaning' }, [currentInterp.meaning]),
      el('div', { className: 'current-status-implication' }, [currentInterp.implication]),
    ]) : null,
    
    currentPlaybook ? el('div', { className: 'intel-card' }, [
      el('h4', { className: 'intel-card__title' }, ['📋 Suggested Actions']),
      el('div', { className: 'quick-actions' },
        currentPlaybook.actions.slice(0, 3).map(action => 
          el('div', { className: 'quick-action', style: `border-left-color: ${statusCfg.color}` }, [action])
        )
      ),
    ]) : null,
    
    intel?.relatedSignals?.length ? el('div', { className: 'intel-card' }, [
      el('h4', { className: 'intel-card__title' }, ['🔀 Related Signals']),
      el('div', { className: 'related-signals' },
        intel.relatedSignals.map(signalId => 
          el('span', { className: 'related-signal-tag' }, [signalId.replace(/_/g, ' ')])
        )
      ),
    ]) : null,
    
    signal.formula ? el('div', { className: 'intel-card' }, [
      el('h4', { className: 'intel-card__title' }, ['🧮 Formula']),
      el('code', { className: 'formula-code' }, [signal.formula]),
      signal.inputs?.length > 0 ? el('div', { className: 'formula-inputs' }, [
        el('span', { className: 'formula-inputs__label' }, ['Data Sources:']),
        ...signal.inputs.map(seriesId =>
          el('a', {
            className: 'formula-input-link',
            href: `https://fred.stlouisfed.org/series/${seriesId}`,
            target: '_blank',
            rel: 'noopener',
          }, [seriesId])
        ),
      ]) : null,
    ]) : null,
  ].filter(Boolean));
}

function buildLevelsTab(intel, status) {
  if (!intel?.interpretation) {
    return el('div', { className: 'tab-content tab-content--empty' }, ['No level data available']);
  }
  
  return el('div', { className: 'tab-content tab-content--levels' }, [
    el('p', { className: 'tab-intro' }, ['Understanding what each level means and when to take action.']),
    el('div', { className: 'levels-grid' },
      intel.interpretation.map(item => {
        const cfg = STATUS_CONFIG[item.level] || STATUS_CONFIG.normal;
        const isActive = item.level === status;
        return el('div', { 
          className: `level-card ${isActive ? 'level-card--active' : ''}`,
          style: isActive ? `border-color: ${cfg.color}; background: ${cfg.bg}` : ''
        }, [
          el('div', { className: 'level-card__header' }, [
            el('span', { className: 'level-card__icon' }, [cfg.icon]),
            el('span', { className: 'level-card__level', style: `color: ${cfg.color}` }, [item.level.toUpperCase()]),
            isActive ? el('span', { className: 'level-card__current' }, ['← CURRENT']) : null,
          ]),
          el('div', { className: 'level-card__range' }, [item.range]),
          el('div', { className: 'level-card__meaning' }, [item.meaning]),
          el('div', { className: 'level-card__implication' }, [item.implication]),
        ]);
      })
    ),
  ]);
}

function buildPlaybookTab(intel, status) {
  if (!intel?.playbook?.length) {
    return el('div', { className: 'tab-content tab-content--empty' }, ['No playbook available']);
  }
  
  return el('div', { className: 'tab-content tab-content--playbook' }, [
    el('p', { className: 'tab-intro' }, ['Actionable strategies for each signal level.']),
    el('div', { className: 'playbook-grid' },
      intel.playbook.map(pb => {
        const cfg = STATUS_CONFIG[pb.level] || STATUS_CONFIG.normal;
        const isActive = pb.level === status;
        return el('div', { 
          className: `playbook-card ${isActive ? 'playbook-card--active' : ''}`,
          style: `border-left-color: ${cfg.color}`
        }, [
          el('div', { className: 'playbook-card__header' }, [
            el('span', { className: 'playbook-card__icon' }, [cfg.icon]),
            el('span', { className: 'playbook-card__level', style: `color: ${cfg.color}` }, [`At ${pb.level.toUpperCase()}`]),
            isActive ? el('span', { className: 'playbook-card__current' }, ['CURRENT']) : null,
          ]),
          el('ul', { className: 'playbook-card__actions' },
            pb.actions.map(action => el('li', {}, [action]))
          ),
        ]);
      })
    ),
  ]);
}

function buildHistoryTab(intel) {
  if (!intel?.historicalEvents?.length) {
    return el('div', { className: 'tab-content tab-content--empty' }, ['No historical data available']);
  }
  
  return el('div', { className: 'tab-content tab-content--history' }, [
    el('p', { className: 'tab-intro' }, ['Notable past events when this signal fired.']),
    el('div', { className: 'history-timeline' },
      intel.historicalEvents.map(event => 
        el('div', { className: 'history-event' }, [
          el('div', { className: 'history-event__marker' }, []),
          el('div', { className: 'history-event__content' }, [
            el('div', { className: 'history-event__header' }, [
              el('span', { className: 'history-event__date' }, [event.date]),
              el('span', { className: 'history-event__value' }, [event.value]),
            ]),
            el('div', { className: 'history-event__title' }, [event.event]),
            el('div', { className: 'history-event__desc' }, [event.description]),
          ]),
        ])
      )
    ),
  ]);
}

function buildCorrelationsTab(intel) {
  if (!intel?.correlations?.length) {
    return el('div', { className: 'tab-content tab-content--empty' }, ['No correlation data available']);
  }
  
  return el('div', { className: 'tab-content tab-content--correlations' }, [
    el('p', { className: 'tab-intro' }, ['How this signal relates to other market indicators.']),
    el('div', { className: 'correlations-list' },
      intel.correlations.map(corr => {
        const corrColor = corr.correlation > 0 ? 'var(--tg-fog-green)' : 'var(--tg-rust-red)';
        const corrPercent = Math.abs(corr.correlation * 100).toFixed(0);
        return el('div', { className: 'correlation-row' }, [
          el('div', { className: 'correlation-row__main' }, [
            el('span', { className: 'correlation-row__signal' }, [corr.signal]),
            el('div', { className: 'correlation-row__bar-wrap' }, [
              el('div', { className: 'correlation-row__bar', style: `width: ${corrPercent}%; background: ${corrColor}` }, []),
            ]),
            el('span', { className: 'correlation-row__value', style: `color: ${corrColor}` }, 
              [`${corr.correlation > 0 ? '+' : ''}${(corr.correlation * 100).toFixed(0)}%`]),
          ]),
          el('div', { className: 'correlation-row__lag' }, [corr.leadLag]),
        ]);
      })
    ),
  ]);
}

/**
 * Build stats box with range-aware labels
 */
function buildStatsBox(signal, historyData, rangeId) {
  const chartData = toChartData(historyData);
  if (chartData.length < 2) return null;
  
  const values = chartData.map(d => d.value);
  const high = Math.max(...values);
  const low = Math.min(...values);
  const current = signal.current?.value;
  const range = high - low;
  const percentile = range > 0 ? ((current - low) / range * 100).toFixed(0) : 50;
  
  const rangeLabel = getRangeLabel(rangeId);
  
  // Check data availability
  const rangeConfig = TIME_RANGES.find(r => r.id === rangeId);
  const availability = getDataAvailability(chartData, rangeConfig?.days || 90);
  
  return el('div', { className: 'stats-box-wrapper' }, [
    el('div', { className: 'stats-row' }, [
      el('div', { className: 'stat-item' }, [
        el('span', { className: 'stat-item__label' }, [`${rangeLabel} High`]),
        el('span', { className: 'stat-item__value stat-item__value--high' }, [formatValue(high, signal.signal_id)]),
      ]),
      el('div', { className: 'stat-item' }, [
        el('span', { className: 'stat-item__label' }, [`${rangeLabel} Low`]),
        el('span', { className: 'stat-item__value stat-item__value--low' }, [formatValue(low, signal.signal_id)]),
      ]),
      el('div', { className: 'stat-item stat-item--percentile' }, [
        el('span', { className: 'stat-item__label' }, ['Percentile']),
        el('div', { className: 'percentile-bar' }, [
          el('div', { className: 'percentile-bar__track' }, [
            el('div', { className: 'percentile-bar__fill', style: `width: ${percentile}%` }, []),
            el('div', { className: 'percentile-bar__marker', style: `left: ${percentile}%` }, []),
          ]),
          el('span', { className: 'percentile-bar__value' }, [`${percentile}%`]),
        ]),
      ]),
    ]),
    // Data availability message
    availability.message ? el('div', { className: 'stats-availability' }, [
      el('span', { className: 'stats-availability__text' }, [availability.message]),
    ]) : null,
  ].filter(Boolean));
}

function buildRegimeDisplay(signal) {
  const level = signal.current?.level || 'neutral';
  const levelCfg = STATUS_CONFIG[level] || STATUS_CONFIG.neutral;
  const explanations = {
    tight: 'Liquidity conditions are constrained. Risk assets may face headwinds.',
    neutral: 'Liquidity conditions are balanced. Normal market dynamics expected.',
    loose: 'Abundant liquidity in the system. Generally supportive for risk assets.',
  };
  
  return el('div', { className: 'regime-display' }, [
    el('div', { className: 'regime-display__state', style: `color: ${levelCfg.color}` }, [level.toUpperCase()]),
    el('div', { className: 'regime-display__explanation' }, [explanations[level] || '']),
  ]);
}

// ===========================================================================
// Main Modal
// ===========================================================================

export async function openExpandedChartModal(signal, initialHistory = []) {
  closeExpandedChartModal();
  currentTab = 'overview';
  currentRangeId = '90d';

  const isRegimeSignal = REGIME_SIGNALS.includes(signal.signal_id);
  const status = signal.current?.level || 'normal';
  const statusCfg = STATUS_CONFIG[status] || STATUS_CONFIG.normal;
  const intel = getSignalIntelligence(signal.signal_id);

  // Build containers
  const chartContainer = el('div', { className: 'chart-modal__chart' }, []);
  const statsContainer = el('div', { className: 'chart-modal__stats' }, []);
  const tabContentContainer = el('div', { className: 'chart-modal__tab-content' }, []);
  
  // Reset export state
  currentChartData = [];
  currentNarrative = null;
  currentStats = null;
  
  // Export handler
  async function handleExport(format) {
    const svgElement = chartContainer.querySelector('svg');
    
    switch (format) {
      case 'png':
        if (!svgElement) throw new Error('Chart not ready');
        await exportPNG(svgElement, signal, currentRangeId);
        break;
      case 'csv':
        if (currentChartData.length === 0) throw new Error('No data available');
        exportCSV(currentChartData, signal, currentRangeId);
        break;
      case 'json':
        if (currentChartData.length === 0) throw new Error('No data available');
        exportJSON(currentChartData, signal, currentRangeId, currentStats, currentNarrative);
        break;
      case 'pdf':
        if (!svgElement) throw new Error('Chart not ready');
        await exportPDF(svgElement, currentChartData, signal, currentRangeId, currentStats, currentNarrative);
        break;
      default:
        throw new Error(`Unknown format: ${format}`);
    }
  }
  
  // Create export dropdown
  const exportDropdown = !isRegimeSignal ? createExportDropdown(handleExport) : null;
  
  // Tab switching function
  function switchTab(tabId) {
    currentTab = tabId;
    
    overlay.querySelectorAll('.tab-btn').forEach(btn => {
      btn.classList.toggle('tab-btn--active', btn.dataset.tab === tabId);
    });
    
    tabContentContainer.innerHTML = '';
    let content;
    switch (tabId) {
      case 'overview': content = buildOverviewTab(intel, signal, status); break;
      case 'levels': content = buildLevelsTab(intel, status); break;
      case 'playbook': content = buildPlaybookTab(intel, status); break;
      case 'history': content = buildHistoryTab(intel); break;
      case 'correlations': content = buildCorrelationsTab(intel); break;
      default: content = buildOverviewTab(intel, signal, status);
    }
    tabContentContainer.appendChild(content);
  }

  // Build tabs - filter out empty ones
  const availableTabs = TABS.filter(tab => {
    if (tab.id === 'overview') return true;
    if (tab.id === 'levels') return intel?.interpretation?.length > 0;
    if (tab.id === 'playbook') return intel?.playbook?.length > 0;
    if (tab.id === 'history') return intel?.historicalEvents?.length > 0;
    if (tab.id === 'correlations') return intel?.correlations?.length > 0;
    return false;
  });

  const tabsBar = el('div', { className: 'chart-modal__tabs' },
    availableTabs.map(tab => 
      el('button', { 
        className: `tab-btn ${tab.id === 'overview' ? 'tab-btn--active' : ''}`,
        'data-tab': tab.id,
        onclick: () => switchTab(tab.id),
      }, [
        el('span', { className: 'tab-btn__icon' }, [tab.icon]),
        el('span', { className: 'tab-btn__label' }, [tab.label]),
      ])
    )
  );

  // Check if this signal has historical events
  const hasHistoricalEvents = intel?.historicalEvents?.length > 0;
  
  // Events toggle button (only if signal has events)
  const eventsToggleBtn = hasHistoricalEvents && !isRegimeSignal
    ? el('button', {
        className: 'events-toggle-btn events-toggle-btn--active',
        title: 'Toggle historical events on chart',
        onclick: () => {
          if (activeChart?.toggleEvents) {
            const isVisible = activeChart.toggleEvents();
            eventsToggleBtn.classList.toggle('events-toggle-btn--active', isVisible);
            eventsToggleBtn.textContent = isVisible ? '📍 Events' : '📍';
          }
        },
      }, ['📍 Events'])
    : null;
  
  // Time controls with all ranges
  const timeControls = isRegimeSignal 
    ? null
    : el('div', { className: 'chart-modal__time-range' }, [
        ...TIME_RANGES.map(range => 
          el('button', { 
            className: `range-btn ${range.id === '90d' ? 'range-btn--active' : ''}`,
            'data-range': range.id,
            onclick: () => loadChartData(range.id),
          }, [range.label])
        ),
        eventsToggleBtn,
      ].filter(Boolean)
      );

  // Value display
  const valueDisplay = isRegimeSignal
    ? el('span', { className: 'chart-modal__current-value', style: `color: ${statusCfg.color}` }, [status.toUpperCase()])
    : el('span', { className: 'chart-modal__current-value', style: `color: ${statusCfg.color}` }, [formatValue(signal.current?.value, signal.signal_id)]);

  // Main modal structure
  const overlay = el('div', { className: 'chart-modal-overlay' }, [
    el('div', { className: 'chart-modal chart-modal--tabbed' }, [
      // Header
      el('div', { className: 'chart-modal__header' }, [
        el('div', { className: 'chart-modal__header-left' }, [
          el('div', { className: 'chart-modal__title-row' }, [
            el('h2', { className: 'chart-modal__title' }, [signal.name || signal.signal_id]),
            el('span', { className: 'chart-modal__status', style: `color: ${statusCfg.color}; background: ${statusCfg.bg}; border-color: ${statusCfg.color}` }, [statusCfg.label]),
          ]),
          signal.description ? el('p', { className: 'chart-modal__subtitle' }, [signal.description]) : null,
        ].filter(Boolean)),
        el('div', { className: 'chart-modal__header-right' }, [
          !isRegimeSignal ? el('button', { 
            className: 'chart-modal__chart-mode-btn',
            title: 'Open in Chart Mode for multi-signal analysis',
            onclick: () => {
              closeExpandedChartModal();
              navigateToChartMode([signal.signal_id], { range: currentRangeId });
            },
          }, ['📊 Chart Mode']) : null,
          exportDropdown,
          el('button', { className: 'chart-modal__close', onclick: closeExpandedChartModal }, ['✕']),
        ].filter(Boolean)),
      ]),

      // Value + Stats Row
      el('div', { className: 'chart-modal__value-row' }, [
        el('div', { className: 'chart-modal__value-section' }, [
          el('span', { className: 'chart-modal__current-label' }, [isRegimeSignal ? 'CURRENT REGIME' : 'CURRENT VALUE']),
          valueDisplay,
          el('span', { className: 'chart-modal__date' }, [`as of ${signal.current?.date || '—'}`]),
        ]),
        statsContainer,
        timeControls,
      ].filter(Boolean)),

      // Chart
      el('div', { className: 'chart-modal__chart-wrapper' }, [chartContainer]),
      
      // AI Narrative Section (hidden for regime signals)
      isRegimeSignal ? null : el('div', { className: 'chart-modal__narrative', id: 'chart-narrative' }, [
        el('div', { className: 'narrative-loading' }, [
          el('span', { className: 'narrative-loading__icon' }, ['🤖']),
          el('span', { className: 'narrative-loading__text' }, ['Generating analysis...']),
        ]),
      ]),
      
      // Tabs
      tabsBar,
      
      // Tab Content
      tabContentContainer,
    ]),
  ]);

  // Chart loading function
  async function loadChartData(rangeId) {
    currentRangeId = rangeId;
    const rangeConfig = TIME_RANGES.find(r => r.id === rangeId);
    const days = rangeConfig?.days || 90;
    
    chartContainer.innerHTML = '<div class="chart-loading">Loading chart...</div>';
    statsContainer.innerHTML = '';
    
    // Update button states
    overlay.querySelectorAll('.range-btn').forEach(btn => {
      btn.classList.toggle('range-btn--active', btn.dataset.range === rangeId);
    });

    try {
      const response = await fetchSignalHistory(signal.signal_id, { days });
      const rawData = response.data || response || [];
      const chartData = toChartData(rawData);

      if (chartData.length === 0) {
        chartContainer.innerHTML = '<div class="chart-empty">No data available</div>';
        return;
      }

      chartContainer.innerHTML = '';
      if (activeChart?.destroy) activeChart.destroy();
      
      // Store chart data for exports
      currentChartData = chartData;
      
      // Pass rangeId and historical events to chart
      activeChart = createExpandedChart(chartContainer, chartData, { 
        status, 
        height: 200,
        rangeId: rangeId,
        historicalEvents: intel?.historicalEvents || [],
        showEvents: showEventsEnabled,
      });
      
      // Update events toggle button state
      if (eventsToggleBtn && activeChart) {
        const visibleCount = activeChart.getVisibleEventsCount();
        if (visibleCount > 0) {
          eventsToggleBtn.classList.add('events-toggle-btn--active');
          eventsToggleBtn.textContent = `📍 ${visibleCount}`;
          eventsToggleBtn.title = `${visibleCount} historical event${visibleCount > 1 ? 's' : ''} visible`;
        } else {
          eventsToggleBtn.textContent = '📍';
          eventsToggleBtn.title = 'No events in this date range';
        }
      }
      
      // Update stats with range-aware labels
      const statsBox = buildStatsBox(signal, rawData, rangeId);
      if (statsBox) {
        statsContainer.innerHTML = '';
        statsContainer.appendChild(statsBox);
      }
      
      // Store stats for exports
      const values = chartData.map(d => d.value);
      currentStats = {
        high: Math.max(...values),
        low: Math.min(...values),
        percentile: values.length > 0 ? Math.round(((signal.current?.value - Math.min(...values)) / (Math.max(...values) - Math.min(...values))) * 100) : 0,
      };
      
      // Generate AI narrative (async, non-blocking)
      updateNarrative(signal, chartData, rangeId);
      
    } catch (err) {
      console.error('[Modal] Failed to load chart:', err);
      chartContainer.innerHTML = `<div class="chart-error">Failed to load: ${err.message}</div>`;
    }
  }
  
  // Narrative update function
  async function updateNarrative(signal, chartData, rangeId) {
    const narrativeContainer = overlay.querySelector('#chart-narrative');
    if (!narrativeContainer) return;
    
    // Show loading state with typing animation
    narrativeContainer.innerHTML = '';
    narrativeContainer.appendChild(
      el('div', { className: 'narrative-loading' }, [
        el('span', { className: 'narrative-loading__icon narrative-loading__icon--typing' }, ['🤖']),
        el('span', { className: 'narrative-loading__text' }, ['Analyzing signal data...']),
        el('div', { className: 'narrative-loading__dots' }, [
          el('span', {}, ['.']),
          el('span', {}, ['.']),
          el('span', {}, ['.']),
        ]),
      ])
    );
    
    try {
      const result = await generateNarrative(signal.signal_id, chartData, rangeId, signal);
      
      if (result.error) {
        narrativeContainer.innerHTML = '';
        narrativeContainer.appendChild(
          el('div', { className: 'narrative-error' }, [
            el('span', { className: 'narrative-error__icon' }, ['⚠️']),
            el('span', { className: 'narrative-error__text' }, [result.error]),
          ])
        );
        return;
      }
      
      if (result.text) {
        // Store narrative for exports
        currentNarrative = result.text;
        
        narrativeContainer.innerHTML = '';
        narrativeContainer.appendChild(
          el('div', { className: 'narrative-content' }, [
            el('div', { className: 'narrative-content__header' }, [
              el('span', { className: 'narrative-content__icon' }, ['🤖']),
              el('span', { className: 'narrative-content__label' }, ['AI Analysis']),
              result.cached ? el('span', { className: 'narrative-content__cached' }, ['cached']) : null,
            ].filter(Boolean)),
            el('p', { className: 'narrative-content__text' }, [result.text]),
          ])
        );
      }
    } catch (err) {
      console.error('[Narrative] Failed:', err);
      narrativeContainer.innerHTML = '';
      narrativeContainer.appendChild(
        el('div', { className: 'narrative-error' }, [
          el('span', { className: 'narrative-error__icon' }, ['⚠️']),
          el('span', { className: 'narrative-error__text' }, ['Unable to generate analysis']),
        ])
      );
    }
  }

  document.body.appendChild(overlay);
  activeModal = overlay;

  requestAnimationFrame(() => {
    overlay.classList.add('chart-modal-overlay--visible');
    overlay.querySelector('.chart-modal').classList.add('chart-modal--visible');
  });

  const handleEsc = (e) => { if (e.key === 'Escape') closeExpandedChartModal(); };
  document.addEventListener('keydown', handleEsc);
  overlay._escHandler = handleEsc;
  overlay.addEventListener('click', (e) => { if (e.target === overlay) closeExpandedChartModal(); });

  // Initialize content
  if (isRegimeSignal) {
    chartContainer.innerHTML = '';
    chartContainer.appendChild(buildRegimeDisplay(signal));
  } else {
    loadChartData('90d');
  }
  
  switchTab('overview');
}

export function closeExpandedChartModal() {
  if (!activeModal) return;
  if (activeChart?.destroy) { activeChart.destroy(); activeChart = null; }
  if (activeModal._escHandler) document.removeEventListener('keydown', activeModal._escHandler);
  
  activeModal.classList.remove('chart-modal-overlay--visible');
  activeModal.querySelector('.chart-modal')?.classList.remove('chart-modal--visible');
  setTimeout(() => { activeModal?.remove(); activeModal = null; }, 200);
}
